import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { UnauthorizedFunction } from '../../utils/UtilsFunction';

/**
 * Build the query string for the library API.
 * Supports: type, industry, fileSizeMin, fileSizeMax, search, page, limit
 * When `search` is present, uses the /library/search endpoint with `query` param.
 */
const buildLibraryUrl = ({ type, industry, fileSizeMin, fileSizeMax, search, page = 1, limit = 24 }) => {
  if (search && search.trim()) {
    // Use the dedicated search endpoint → /library/search?query=...
    const base = process.env.REACT_APP_BASE_URL + 'library/search';
    const params = new URLSearchParams();
    params.set('query', search.trim());
    if (type) params.set('type', type);
    if (industry) params.set('industry', industry);
    if (fileSizeMin != null) params.set('fileSizeMin', fileSizeMin);
    if (fileSizeMax != null) params.set('fileSizeMax', fileSizeMax);
    params.set('page', page);
    params.set('limit', limit);
    const url = `${base}?${params.toString()}`;
    console.log('[Library] search URL:', url);
    return url;
  }

  // Default browse endpoint → /library?isPublic=true&...
  const base = process.env.REACT_APP_BASE_URL + 'library';
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  if (industry) params.set('industry', industry);
  if (fileSizeMin != null) params.set('fileSizeMin', fileSizeMin);
  if (fileSizeMax != null) params.set('fileSizeMax', fileSizeMax);
  params.set('isPublic', 'true');
  params.set('page', page);
  params.set('limit', limit);
  const url = `${base}?${params.toString()}`;
  console.log('[Library] browse URL:', url);
  return url;
};


export const fetch_library_page = createAsyncThunk(
  'liabrary/fetchPage',
  async (params, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      console.log('[fetch_library_page] called with params:', params);
      const url = buildLibraryUrl(params);
      console.log('[fetch_library_page] final URL:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await response.json();
      if (!res.success) UnauthorizedFunction(res);
      return { ...res, page: params.page ?? 1 };
    } catch (error) {
      console.error('Error fetching library page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Legacy thunk kept for backward compatibility with other consumers.
*/
export const get_liabrary_data = createAsyncThunk(
  'liabrary_data',
  async (data, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        process.env.REACT_APP_BASE_URL + `library?type=${data}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
      );
      const res = await response.json();
      if (!res.success) UnauthorizedFunction(res);
      return res;
    } catch (error) {
      console.error('Error fetching library data:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Search thunk — kept for backward compatibility.
 */
export const search_liabrary_data = createAsyncThunk(
  'liabrary_search',
  async (query, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}library/search?query=${encodeURIComponent(query)}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
      );
      const res = await response.json();
      if (!res.success) UnauthorizedFunction(res);
      return {
        success: res.success,
        data: res.data.length > 0 ? [{ subtype: 'search_results', data: res.data }] : [],
        total: res.total,
      };
    } catch (error) {
      console.error('Error searching library data:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Get a Sketchfab model's download URL via the backend proxy.
 * Pass the model uid as the argument.
 * Returns { url, expires } or throws on failure.
 */
export const fetch_sketchfab_download_url = async (uid) => {
  const token = localStorage.getItem('token');
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}sketchfab/models/${encodeURIComponent(uid)}/download`,
    { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
  );
  const res = await response.json();
  if (!response.ok || !res.success) {
    throw new Error(res?.message || res?.error || `Failed to get Sketchfab download URL (${response.status})`);
  }
  // Backend should return { success: true, data: { url, expires } }
  // or { success: true, url }
  return res.data?.url ?? res.url ?? null;
};

/**
 * Fetch Sketchfab models via the backend proxy.
 * Pass { q, categories, tags, cursor, isFirstPage } as the argument.
 */
export const fetch_sketchfab_page = createAsyncThunk(
  'liabrary/fetchSketchfab',
  async ({ q = '', categories = '', tags = '', cursor = null, isFirstPage = true }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (categories) params.set('categories', categories);
      if (tags) params.set('tags', tags);
      if (cursor) params.set('cursor', cursor);

      const url = `${process.env.REACT_APP_BASE_URL}sketchfab/models/search?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await response.json();
      return { ...res, isFirstPage };
    } catch (error) {
      console.error('Error fetching Sketchfab models:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  liabrary_cards: [],
  // grouped array kept for legacy consumers
  liabrary_data: [],
  liabrary_loading: false,
  liabrary_loading_more: false,
  total_count: 0,
  current_page: 1,
  has_more: false,
  // Sketchfab results
  sketchfab_cards: [],
  sketchfab_loading: false,
  sketchfab_loading_more: false,
  sketchfab_has_more: false,
  sketchfab_cursor: null,   // Sketchfab uses cursor-based pagination
};

const liabrarySlice = createSlice({
  name: 'liabrary_data',
  initialState,
  reducers: {
    reset_library: (state) => {
      state.liabrary_cards = [];
      state.liabrary_data = [];
      state.current_page = 1;
      state.has_more = false;
      state.total_count = 0;
    },
    reset_sketchfab: (state) => {
      state.sketchfab_cards = [];
      state.sketchfab_loading = false;
      state.sketchfab_loading_more = false;
      state.sketchfab_has_more = false;
      state.sketchfab_cursor = null;
    },
  },
  extraReducers: (builder) => {
    // ── paginated fetch ──────────────────────────────────────────────────
    builder
      .addCase(fetch_library_page.pending, (state, action) => {
        const isFirstPage = (action.meta.arg?.page ?? 1) === 1;
        if (isFirstPage) {
          state.liabrary_loading = true;
          state.liabrary_loading_more = false;
        } else {
          state.liabrary_loading_more = true;
        }
      })
      .addCase(fetch_library_page.fulfilled, (state, action) => {
        state.liabrary_loading = false;
        state.liabrary_loading_more = false;
        if (!action.payload?.success) return;

        // The API may return a flat array or grouped array — normalise to flat
        const rawData = action.payload.data ?? [];
        const flat = Array.isArray(rawData)
          ? rawData.flatMap((g) => (g?.data ? g.data : [g]))
          : [];

        const isFirstPage = (action.payload.page ?? 1) === 1;
        state.liabrary_cards = isFirstPage ? flat : [...state.liabrary_cards, ...flat];

        state.total_count = action.payload.total ?? 0;
        state.current_page = action.payload.page ?? 1;
        // has_more: more pages exist if we got a full page worth of results
        state.has_more = flat.length > 0 && state.liabrary_cards.length < (action.payload.total ?? 0);
      })
      .addCase(fetch_library_page.rejected, (state) => {
        state.liabrary_loading = false;
        state.liabrary_loading_more = false;
      });

    // ── legacy get_liabrary_data ─────────────────────────────────────────
    builder
      .addCase(get_liabrary_data.pending, (state) => {
        state.liabrary_loading = true;
      })
      .addCase(get_liabrary_data.fulfilled, (state, action) => {
        state.liabrary_loading = false;
        state.liabrary_data = action.payload?.success ? action.payload.data : [];
        state.total_count = action.payload?.success ? action.payload.total : 0;
      })
      .addCase(get_liabrary_data.rejected, (state) => {
        state.liabrary_loading = false;
      });

    // ── legacy search ────────────────────────────────────────────────────
    builder
      .addCase(search_liabrary_data.pending, (state) => {
        state.liabrary_loading = true;
      })
      .addCase(search_liabrary_data.fulfilled, (state, action) => {
        state.liabrary_loading = false;
        state.liabrary_data = action.payload?.success ? action.payload.data : [];
        state.total_count = action.payload?.success ? action.payload.total : 0;
      })
      .addCase(search_liabrary_data.rejected, (state) => {
        state.liabrary_loading = false;
      });

    // ── Sketchfab ────────────────────────────────────────────────────────
    builder
      .addCase(fetch_sketchfab_page.pending, (state, action) => {
        if (action.meta.arg?.isFirstPage !== false) {
          state.sketchfab_loading = true;
          state.sketchfab_loading_more = false;
        } else {
          state.sketchfab_loading_more = true;
        }
      })
      .addCase(fetch_sketchfab_page.fulfilled, (state, action) => {
        state.sketchfab_loading = false;
        state.sketchfab_loading_more = false;
        // Response shape: { success, data: { models: [...], hasMore, ... } }
        const payload = action.payload;
        const results = payload?.data?.models ?? payload?.results ?? payload?.data ?? [];
        const isFirstPage = payload?.isFirstPage !== false;

        if (isFirstPage) {
          // Deduplicate by uid on first page too (API may return dupes)
          const seen = new Set();
          state.sketchfab_cards = results.filter((m) => {
            const key = m.uid ?? m.id;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        } else {
          // Append only items whose uid isn't already in the list
          const existingUids = new Set(state.sketchfab_cards.map((m) => m.uid ?? m.id));
          const newItems = results.filter((m) => {
            const key = m.uid ?? m.id;
            return key && !existingUids.has(key);
          });
          state.sketchfab_cards = [...state.sketchfab_cards, ...newItems];
        }

        // hasMore from nested data object
        const hasMore = payload?.data?.hasMore ?? false;
        state.sketchfab_cursor = hasMore ? (payload?.data?.cursor ?? payload?.next ?? null) : null;
        state.sketchfab_has_more = hasMore && results.length > 0;
      })
      .addCase(fetch_sketchfab_page.rejected, (state) => {
        state.sketchfab_loading = false;
        state.sketchfab_loading_more = false;
      });
  },
});

export const { reset_library, reset_sketchfab } = liabrarySlice.actions;
export default liabrarySlice.reducer;